/**
 * Taking Dr Cleanstein's answers out of the portal in forms people already use:
 * a table as an Excel file, one answer or a whole chat as a Word document, and
 * plain text for the clipboard.
 *
 * The Word file is a Word-flavoured HTML document saved as .doc, which Word,
 * Google Docs and Pages all open. It needs no extra package. Every piece of
 * answer text is escaped before it goes in, and only paragraphs, lists,
 * headings, bold and tables are written, so nothing in an answer (which can
 * quote what the public typed into a lead form) can become a link or an image
 * in the document.
 */

import { column, downloadExcel, fmt, type ExportField, type FieldKind, type Row } from '../../lib/exportFile';
import { parseAnswer, plainCell, type AnswerTable } from './AskAnswer';
import { formatDate } from '../../utils/formatters';
import type { DisplayMessage } from './askApi';
import { STAFF_ASSISTANT_NAME } from '../../config/assistant';

const MONTHS: Record<string, string> = {
  jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
  jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12',
};

/** "3 Dec 2025" or "2025-12-03" to "2025-12-03"; anything else to null. */
export function cellDate(v: string): string | null {
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (iso) return v;
  const uk = /^(\d{1,2}) ([A-Za-z]{3})[a-z]* (\d{4})$/.exec(v);
  if (!uk) return null;
  const month = MONTHS[(uk[2] ?? '').toLowerCase()];
  return month ? `${uk[3]}-${month}-${(uk[1] ?? '').padStart(2, '0')}` : null;
}

/** "R46,220.50" or "46220.5" to 46220.5; anything else to null. */
export function cellNumber(v: string): number | null {
  const cleaned = v.replace(/^R\s?/, '').replace(/[,\s]/g, '');
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return null;
  return Number(cleaned);
}

/** What a column holds, judged from every filled cell, so Excel can sum and sort it. */
export function columnKind(cells: string[]): FieldKind {
  const filled = cells.map(plainCell).map((c) => c.trim()).filter((c) => c !== '' && c !== '-');
  if (filled.length === 0) return 'text';
  if (filled.every((c) => cellDate(c))) return 'date';
  // A phone number is digits too, and must keep its leading zero, so only money
  // or short plain counts become numbers.
  if (filled.every((c) => /^R\s?-?[\d,]+(\.\d+)?$/.test(c))) return 'money';
  // A leading zero (01234) marks a code, not a number.
  if (filled.every((c) => /^-?(0|[1-9]\d{0,5})(\.\d+)?$/.test(c))) return 'number';
  return 'text';
}

export async function downloadTable(table: AnswerTable, fileName: string): Promise<void> {
  const kinds = table.headers.map((_, i) => columnKind(table.rows.map((r) => r[i] ?? '')));
  const fields: ExportField<null>[] = table.headers.map((h, i) => {
    const kind = kinds[i] ?? 'text';
    const read = fmt(kind, (v) => {
      const s = plainCell(String(v ?? '')).trim();
      if (s === '' || s === '-') return null;
      if (kind === 'date') return cellDate(s);
      if (kind === 'money' || kind === 'number') return cellNumber(s);
      return s;
    });
    return column<null>(`c${i}`, plainCell(h), read);
  });
  const rows: Row[] = table.rows.map((r) => Object.fromEntries(r.map((c, i) => [`c${i}`, c])));
  await downloadExcel(rows, fields, null, fileName, STAFF_ASSISTANT_NAME);
}

// ---------------------------------------------------------------- Word

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Escapes first, then turns **bold** into <b>; nothing else becomes markup. */
function inlineHtml(s: string): string {
  return esc(s.replace(/`/g, '')).replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>');
}

function answerHtml(text: string): string {
  return parseAnswer(text)
    .map((b) => {
      if (b.kind === 'h') return `<h3>${inlineHtml(b.text)}</h3>`;
      if (b.kind === 'ul' || b.kind === 'ol') {
        const items = b.items.map((it) => `<li>${inlineHtml(it)}</li>`).join('');
        return `<${b.kind}>${items}</${b.kind}>`;
      }
      if (b.kind === 'table') {
        const head = b.table.headers.map((h) => `<th>${inlineHtml(h)}</th>`).join('');
        const body = b.table.rows.map((r) => `<tr>${r.map((c) => `<td>${inlineHtml(c)}</td>`).join('')}</tr>`).join('');
        return `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
      }
      return `<p>${b.lines.map(inlineHtml).join('<br>')}</p>`;
    })
    .join('\n');
}

function wordDocument(title: string, bodyHtml: string): string {
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${esc(title)}</title>
<!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>100</w:Zoom></w:WordDocument></xml><![endif]-->
<style>
body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; color: #1e293b; }
h1 { font-size: 16pt; color: #772cbb; margin: 0 0 4pt; }
h2 { font-size: 12pt; margin: 14pt 0 4pt; }
h3 { font-size: 11pt; margin: 10pt 0 4pt; }
p { margin: 0 0 8pt; }
.meta { color: #64748b; font-size: 9pt; margin-bottom: 14pt; }
.question { background: #f3e8ff; padding: 6pt 8pt; margin: 12pt 0 6pt; }
table { border-collapse: collapse; margin: 4pt 0 10pt; }
th, td { border: 1px solid #cbd5e1; padding: 3pt 6pt; font-size: 9.5pt; text-align: left; }
th { background: #f1f5f9; }
</style></head>
<body>${bodyHtml}</body></html>`;
}

function saveFile(fileName: string, content: string, type: string) {
  const blob = new Blob(['﻿', content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function stamp(): string {
  return formatDate(new Date());
}

/** The local date, so a file saved after 22:00 in Denmark is not named for tomorrow in UTC. */
export function fileDate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function downloadAnswerDoc(question: string | null, answer: string) {
  const body = `<h1>${STAFF_ASSISTANT_NAME}</h1><div class="meta">Saved ${esc(stamp())} from the CleanMyBed portal</div>
${question ? `<div class="question"><b>Question:</b> ${inlineHtml(question)}</div>` : ''}
${answerHtml(answer)}`;
  saveFile(`${STAFF_ASSISTANT_NAME.toLowerCase()}-answer-${fileDate()}.doc`, wordDocument(`${STAFF_ASSISTANT_NAME} answer`, body), 'application/msword');
}

export function downloadChatDoc(title: string, messages: DisplayMessage[]) {
  const parts = messages.map((m) =>
    m.role === 'user' ? `<div class="question"><b>Question:</b> ${inlineHtml(m.text)}</div>` : answerHtml(m.text),
  );
  const body = `<h1>${esc(title)}</h1><div class="meta">Chat with ${STAFF_ASSISTANT_NAME}, saved ${esc(stamp())} from the CleanMyBed portal</div>
${parts.join('\n')}`;
  saveFile(`leo-chat-${fileDate()}.doc`, wordDocument(title, body), 'application/msword');
}

/** The answer as tidy plain text: no ** markers, tables as tab-separated lines that paste into Excel. */
export function answerPlainText(text: string): string {
  return parseAnswer(text)
    .map((b) => {
      if (b.kind === 'h') return plainCell(b.text);
      if (b.kind === 'ul') return b.items.map((it) => `- ${plainCell(it)}`).join('\n');
      if (b.kind === 'ol') return b.items.map((it, i) => `${i + 1}. ${plainCell(it)}`).join('\n');
      if (b.kind === 'table') {
        return [b.table.headers, ...b.table.rows].map((r) => r.map(plainCell).join('\t')).join('\n');
      }
      return b.lines.map(plainCell).join('\n');
    })
    .join('\n\n');
}
